import React, { useState, useEffect, useRef } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { LocationIcon, ShareIcon } from '../components/Icons';
import { db } from '../firebase';
import { useParams, useNavigate } from 'react-router-dom';

const LoadingComponent = () => (
    <div className="flex justify-center items-center h-screen">
        <div className="text-xl text-gray-600">Aguarde, carregando item...</div>
    </div>
);

export default function ItemDetailScreenPublic({ setModal }) {
    // --- Hooks ---
    const { itemId } = useParams();
    const navigate = useNavigate();
    const hasNavigatedAway = useRef(false);

    // --- State Management ---
    const [item, setItem] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedImageIndex, setSelectedImageIndex] = useState(0);

    // Effect to fetch the item's data
    useEffect(() => {
        setLoading(true);
        const itemRef = doc(db, 'items', itemId);
        const unsubscribe = onSnapshot(itemRef, (docSnap) => {
            if (docSnap.exists()) {
                setItem({ id: docSnap.id, ...docSnap.data() });
            } else {
                if (!hasNavigatedAway.current) {
                    console.error("Item not found!");
                    setItem(null);
                }
            }
            setLoading(false);
        });
        return () => unsubscribe();
    }, [itemId]);

    // Effect to handle redirection if the item does not exist or was deleted
    useEffect(() => {
        if (!loading && !item && !hasNavigatedAway.current) {
            hasNavigatedAway.current = true;
            setModal({
                title: "Item Indisponível",
                message: "Este item não está mais disponível ou foi removido.",
                onConfirm: () => {
                    setModal(null);
                    navigate('/'); // Redirect public users to home/landing
                }
            });
        }
    }, [loading, item, navigate, setModal]);

    // --- Handlers ---
    const handleShare = async () => {
        const shareUrl = window.location.href;
        const shareData = {
            title: `Confira este item: ${item.title}`,
            text: `Eu vi "${item.title}" no Escambo Digital e achei que você se interessaria! Veja aqui:`,
            url: shareUrl,
        };
        try {
            if (navigator.share) {
                await navigator.share(shareData);
            } else {
                navigator.clipboard.writeText(shareUrl);
                setModal({
                    title: "Link Copiado",
                    message: "Link do item copiado para a área de transferência!",
                });
            }
        } catch (err) {
            console.error("Error sharing:", err);
        }
    };

    // Guard Clauses
    if (loading) return <LoadingComponent />;
    if (!item) return <LoadingComponent />;
    // Listing held by the photo check (functions/lib/moderation.js): only
    // signed-in owners and admins see it until an admin reviews it.
    if (item.status === 'bloqueado_moderacao') {
        return <div className="max-w-xl mx-auto p-6 mt-8 bg-white rounded-lg shadow text-center"><h1 className="text-xl font-bold text-gray-800">Anúncio em análise</h1><p className="text-gray-600 mt-2">Este anúncio está sendo revisado e não está disponível no momento.</p></div>;
    }

    const imageUrls = item.imageUrls || [];

    return (
        <div className="max-w-4xl mx-auto p-4">
            <button 
                onClick={() => navigate('/')} 
                className="text-green-600 mb-4 inline-flex items-center hover:underline"
            >
                &larr; <span className="ml-2">Voltar à Página Inicial</span>
            </button>
            
            <div className="md:grid md:grid-cols-2 md:gap-8">
                {/* Images Section */}
                <div>
                    {/* Square box reserved before the photo loads (no layout shift); whole photo visible. */}
                    <img
                        src={item.mediumUrls?.[selectedImageIndex] || imageUrls[selectedImageIndex] || 'https://placehold.co/600x400/e2e8f0/64748b?text=Item'}
                        alt={item.title}
                        width="600" height="600" fetchpriority="high" decoding="async"
                        className="w-full aspect-square object-contain bg-gray-100 rounded-lg mb-2"
                    />
                    {imageUrls.length > 1 && (
                        <div className="flex space-x-2">
                            {imageUrls.map((url, index) => (
                                <img
                                    key={index}
                                    src={item.thumbUrls?.[index] || url}
                                    alt={`Foto ${index + 1}`}
                                    width="64" height="64" loading="lazy"
                                    onClick={() => setSelectedImageIndex(index)}
                                    className={`w-16 h-16 object-cover rounded-md cursor-pointer border-2 transition-all ${selectedImageIndex === index ? 'border-green-500 scale-105' : 'border-transparent hover:border-green-300'}`}
                                />
                            ))}
                        </div>
                    )}
                </div>

                {/* Info Section */}
                <div className="bg-white p-6 rounded-lg shadow-md mt-4 md:mt-0 relative flex flex-col">
                    <div className="absolute top-4 right-4 flex items-center space-x-3">
                        <button onClick={handleShare} className="text-gray-500 hover:text-green-600 transition-colors duration-200" title="Compartilhar item">
                            <ShareIcon className="w-6 h-6" />
                        </button>
                    </div>

                    <h1 className="text-3xl font-bold text-gray-800 pr-12">{item.title}</h1>
                    
                    <div className="mt-2">
                        {item.condition && (
                            <span className={`inline-block text-xs font-semibold mr-2 px-2.5 py-0.5 rounded-full ${item.condition === 'new' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`}>
                                {item.condition === 'new' ? 'Novo' : 'Usado'}
                            </span>
                        )}
                        {item.category && (
                            <span className="inline-block bg-gray-200 rounded-full px-3 py-0.5 text-xs font-semibold text-gray-700 mr-2">
                                {item.category}
                            </span>
                        )}
                    </div>

                    <p className="text-3xl font-bold text-green-600 my-4">{item.credits} Créditos</p>
                    
                    {item.location?.city && (
                        <p className="text-sm text-gray-500 mb-4 flex items-center">
                            <LocationIcon className="w-4 h-4 mr-2" />
                            {item.location.city}, {item.location.uf}
                        </p>
                    )}

                    <p className="text-gray-600 mb-8 flex-grow">{item.description}</p>
                    
                    {/* Call to Action for Public Users */}
                    <div className="space-y-3 mt-auto pt-6 border-t border-gray-100">
                        <p className="text-sm text-gray-500 text-center mb-2">
                            Faça login ou cadastre-se para conseguir este item.
                        </p>
                        <button 
                            onClick={() => navigate('/login')} 
                            className="w-full bg-green-600 text-white font-bold py-3 rounded-lg hover:bg-green-700 transition-colors shadow-sm hover:shadow-md"
                        >
                            Faça Login para Trocar
                        </button>
                        <button 
                            onClick={() => navigate('/signup')} 
                            className="w-full bg-white border border-gray-300 text-gray-700 font-bold py-3 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                            Criar uma Conta
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
